import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * Limit a metric catalog to the workspaces a card is scoped to.
 * Empty pick → the app's selected workspace (ScopeWindow's "this board's own").
 * When a scope is active, ONLY metrics that list at least one of those
 * workspace ids are offered — untagged / company-wide rows must not leak in
 * (that was dumping every Manual entry KPI into a Sales-only card).
 */
export const metricsForWorkspaces = (catalog, workspaceIds, fallbackWorkspaceId) => {
  const list = Array.isArray(catalog) ? catalog : [];
  const scope = (Array.isArray(workspaceIds) ? workspaceIds : [])
    .filter(Boolean)
    .map(String);
  let effective = [];
  if (scope.length) effective = scope;
  else if (fallbackWorkspaceId) effective = [String(fallbackWorkspaceId)];
  if (!effective.length) return list;
  return list.filter((m) => {
    /* Derived metrics (Average Daily Revenue, ...) aren't tied to any one
       workspace — they read the company's canonical Total Revenue KPI
       directly — so a workspace scope must never hide them. */
    if (m.derived) return true;
    const mws = Array.isArray(m.workspaceIds) ? m.workspaceIds.map(String).filter(Boolean) : [];
    if (!mws.length) return false;
    return mws.some((id) => effective.includes(id));
  });
};

/** Which of a card's workspaces a metric lives on, joined for a dropdown
 *  label ("Field Sales + In-House Sales") — so a picker spanning several
 *  teams can show which one(s) a given option actually belongs to. */
export const metricWorkspaceTag = (m, workspaces) => {
  const ids = Array.isArray(m?.workspaceIds) ? m.workspaceIds.map(String).filter(Boolean) : [];
  if (!ids.length) return "";
  const names = ids
    .map((id) => ((workspaces || []).find((w) => String(w._id) === id) || {}).name)
    .filter(Boolean);
  return names.join(" + ");
};

/** Safe label for picker group headers — never paint `[object Object]`. */
export const metricGroupLabel = (value, fallback = "Manual entry") => {
  if (value == null || value === "") return fallback;
  if (typeof value === "string") {
    const t = value.trim();
    if (!t || t === "[object Object]") return fallback;
    return t;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    const nested = value.name || value.label || value.title || value.code || value.sourceName;
    if (typeof nested === "string" && nested.trim() && nested !== "[object Object]") {
      return nested.trim();
    }
  }
  return fallback;
};

/**
 * Collapsed dropdown for Scorekeeper metrics — search + source-grouped list.
 */
export const MetricPicker = ({ metrics = [], value, onPick }) => {
  const [open, setOpen] = useState(false);
  const [mq, setMq] = useState("");
  const ref = useRef(null);

  const selected = metrics.find((m) => m.id === value) || null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const groups = useMemo(() => {
    const term = mq.trim().toLowerCase();
    const out = {};
    metrics.forEach((m) => {
      if (term && !(m.label || "").toLowerCase().includes(term)) return;
      const g = metricGroupLabel(m.group);
      (out[g] = out[g] || []).push(m);
    });
    return out;
  }, [metrics, mq]);
  const groupNames = Object.keys(groups);

  const choose = (m) => { onPick(m); setOpen(false); setMq(""); };

  return (
    <div className="gmbw-mpick" ref={ref}>
      <button
        type="button"
        className={`gmbw-mpick-trigger${open ? " open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "gmbw-mpick-val" : "gmbw-mpick-ph"}>
          {selected ? selected.label : "Choose a metric"}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 180ms ease", flexShrink: 0 }}><path d="M6 9l6 6 6-6" /></svg>
      </button>

      {open && (
        <div className="gmbw-mpick-menu" role="listbox">
          <div className="gmbw-msearch">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input type="text" value={mq} onChange={(e) => setMq(e.target.value)} placeholder="Search your metrics" aria-label="Search metrics" autoFocus />
          </div>
          <div className="gmbw-metric-list">
            {!metrics.length && (
              <div className="gmbw-metric-empty">No metrics in this workspace yet — add one in Company Metrics.</div>
            )}
            {metrics.length > 0 && !groupNames.length && (
              <div className="gmbw-metric-empty">No metric matches “{mq}”.</div>
            )}
            {groupNames.map((g) => (
              <div key={g} className="gmbw-metric-group">
                <div className="gmbw-metric-gh">{g}</div>
                {groups[g].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`gmbw-metric-opt${value === m.id ? " on" : ""}`}
                    onClick={() => choose(m)}
                  >
                    <span className="gmbw-metric-name">{m.label}</span>
                    <span className="gmbw-metric-kind">{m.kind}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

MetricPicker.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  metrics: PropTypes.array,
  value: PropTypes.string,
  onPick: PropTypes.func.isRequired,
};
MetricPicker.defaultProps = {
  metrics: [],
  value: null,
};

export default MetricPicker;
